import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Repeat, DollarSign, Compass, ArrowLeft, ArrowRight,
  CheckCircle2, ShieldCheck, Phone, Sparkles,
} from "lucide-react";
import { company } from "../mock";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const goals = [
  { key: "purchase", label: "Purchase a property", desc: "Acquire commercial real estate", icon: Building2 },
  { key: "refinance", label: "Refinance", desc: "Replace my current loan", icon: Repeat },
  { key: "cashout", label: "Working capital / cash", desc: "Fund my business needs", icon: DollarSign },
  { key: "explore", label: "Just exploring", desc: "Not sure which fits yet", icon: Compass },
];

const propertyOptions = ["Commercial / Office", "Apartment / Multifamily", "Retail", "Industrial / Warehouse", "Fix & Flip", "Other"];
const amountOptions = ["Under $250k", "$250k – $1M", "$1M – $5M", "$5M – $25M", "$25M+", "Not sure yet"];
const timelineOptions = ["As soon as possible", "1–3 months", "3–6 months", "Just researching"];
const creditOptions = ["Excellent — 720+", "Good — 660–719", "Fair — 600–659", "Below 600 / rebuilding", "I'm not sure"];

const TOTAL = 6;

export default function Apply() {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    goal: "", property: "", amount: "", credit: "", timeline: "",
    firstName: "", lastName: "", email: "", phone: "", agree: false,
  });
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const recRequested = useRef(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, TOTAL - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const chooseAndNext = (k, v) => {
    set(k, v);
    setTimeout(next, 180);
  };

  useEffect(() => {
    if (step !== 5 || recRequested.current) return;
    recRequested.current = true;
    const hasInput = data.goal || data.property || data.amount || data.credit || data.timeline;
    if (!hasInput) return;
    setRecLoading(true);
    api
      .post("/recommend", {
        goal: data.goal, property: data.property, amount: data.amount,
        credit: data.credit, timeline: data.timeline,
      })
      .then(({ data: r }) => { if (r && r.program) setRec(r); })
      .catch(() => {})
      .finally(() => setRecLoading(false));
  }, [step, data]);

  const submit = async () => {
    if (!data.firstName || !data.email || !data.phone || !data.agree) {
      toast({ title: "Almost there", description: "Please complete your details and accept the consent to continue." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...data, recommended: rec?.program || "" });
      setDone(true);
    } catch (err) {
      toast({ title: "Submission failed", description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.round(((step + 1) / TOTAL) * 100);

  const OptionCard = ({ active, onClick, icon: Icon, label, desc }) => (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border-2 p-5 transition-all flex items-center gap-4 ${
        active ? "border-[#c6a15b] bg-[#c6a15b]/5" : "border-slate-200 hover:border-[#c6a15b]/50 hover:bg-slate-50"
      }`}
    >
      {Icon && (
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-[#c6a15b] text-[#0b1f3a]" : "bg-slate-100 text-[#0b1f3a]"}`}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div>
        <div className="font-semibold text-[#0b1f3a]">{label}</div>
        {desc && <div className="text-sm text-slate-500">{desc}</div>}
      </div>
    </button>
  );

  const Pills = ({ options, value, onPick }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => (
        <OptionCard key={o} active={value === o} onClick={() => onPick(o)} label={o} />
      ))}
    </div>
  );

  return (
    <main className="bg-slate-50 min-h-[80vh] py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Card */}
        <div className="rounded-2xl bg-white shadow-xl ring-1 ring-slate-100 overflow-hidden">
          {/* Header */}
          <div className="bg-[#0b1f3a] px-6 sm:px-8 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-white.png" alt="LTR Capital Partners" className="h-9 w-auto object-contain" />
              <div className="hidden sm:block text-xs text-slate-300 border-l border-white/20 pl-3 leading-tight">Fast pre-qualification<br />no credit pull</div>
            </div>
            {!done && <div className="text-xs text-[#c6a15b] font-semibold">Step {step + 1} of {TOTAL}</div>}
          </div>

          {/* Progress */}
          {!done && (
            <div className="h-1.5 bg-slate-100">
              <div className="h-full bg-[#c6a15b] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          )}

          <div className="p-6 sm:p-10">
            {done ? (
              <div className="text-center py-8">
                <div className="mx-auto h-16 w-16 rounded-full bg-[#c6a15b]/15 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-[#b08e46]" />
                </div>
                <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a] mt-6">You're in good hands.</h2>
                <p className="mt-3 text-slate-600">
                  Thanks {data.firstName}! We've received your details and a lending specialist will
                  reach out shortly — usually the same business day.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0b1f3a] px-6 py-3 font-bold text-white hover:bg-[#13294b] transition">
                    Back to Home
                  </Link>
                  <a href={`tel:${company.phoneTel}`} className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-6 py-3 font-semibold text-[#0b1f3a] hover:bg-slate-50 transition">
                    <Phone className="h-4 w-4" /> Call Us Now
                  </a>
                </div>
              </div>
            ) : (
              <>
                {step === 0 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">Let's see what you qualify for.</h2>
                    <p className="mt-2 text-slate-600">What are you looking to do? No login, no documents yet.</p>
                    <div className="mt-6 space-y-3">
                      {goals.map((g) => (
                        <OptionCard key={g.key} active={data.goal === g.label} onClick={() => chooseAndNext("goal", g.label)} icon={g.icon} label={g.label} desc={g.desc} />
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">What type of property?</h2>
                    <p className="mt-2 text-slate-600">This helps us match the right program.</p>
                    <div className="mt-6"><Pills options={propertyOptions} value={data.property} onPick={(v) => chooseAndNext("property", v)} /></div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">How much funding do you need?</h2>
                    <p className="mt-2 text-slate-600">A rough number is fine — we'll dial it in later.</p>
                    <div className="mt-6"><Pills options={amountOptions} value={data.amount} onPick={(v) => chooseAndNext("amount", v)} /></div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">How's your credit looking?</h2>
                    <p className="mt-2 text-slate-600">No pull happens here — just your best guess.</p>
                    <div className="mt-6"><Pills options={creditOptions} value={data.credit} onPick={(v) => chooseAndNext("credit", v)} /></div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">When do you want to move forward?</h2>
                    <p className="mt-2 text-slate-600">No pressure — this just helps us prioritize.</p>
                    <div className="mt-6"><Pills options={timelineOptions} value={data.timeline} onPick={(v) => chooseAndNext("timeline", v)} /></div>
                  </div>
                )}

                {step === 5 && (
                  <div>
                    <h2 className="font-display font-extrabold text-2xl text-[#0b1f3a]">Where should we send your options?</h2>
                    <p className="mt-2 text-slate-600">We'll review your answers and reach out personally — usually same day.</p>
                    {(recLoading || rec) && (
                      <div data-testid="apply-recommendation" className="mt-5 rounded-xl border border-[#c6a15b]/40 bg-[#c6a15b]/5 p-4">
                        <div className="flex items-center gap-2 text-[#8a6d2f] font-semibold text-sm">
                          <Sparkles className={`h-4 w-4 ${recLoading ? "animate-pulse" : ""}`} />
                          {recLoading ? "Finding your best-fit program..." : "Recommended for you"}
                        </div>
                        {rec && (
                          <div className="mt-2">
                            <div className="font-display font-extrabold text-lg text-[#0b1f3a]" data-testid="apply-rec-program">{rec.program}</div>
                            {rec.reason && <p className="mt-1 text-sm text-slate-600">{rec.reason}</p>}
                            {rec.alternative && (
                              <p className="mt-1.5 text-xs text-slate-500">
                                Also worth considering: <span className="font-semibold text-[#0b1f3a]">{rec.alternative}</span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-6 space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <input value={data.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="First name" className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" />
                        <input value={data.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Last name" className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" />
                      </div>
                      <input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" />
                      <input value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile phone" className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" />
                      <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={data.agree} onChange={(e) => set("agree", e.target.checked)} className="mt-1 h-4 w-4 accent-[#c6a15b]" />
                        <span>I agree to be contacted by LTR Capital Partners by phone, text, or email about my inquiry. This is not a loan application and no credit check is performed.</span>
                      </label>
                      <button onClick={submit} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#c6a15b] px-7 py-3.5 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors disabled:opacity-60">
                        {submitting ? "Submitting..." : "Get My Options"} <ArrowRight className="h-5 w-5" />
                      </button>
                      <p className="flex items-center justify-center gap-2 text-xs text-slate-400">
                        <ShieldCheck className="h-4 w-4 text-[#c6a15b]" /> No SSN, no credit pull, no documents on this form — by design.
                      </p>
                    </div>
                  </div>
                )}

                {/* Nav */}
                {step > 0 && (
                  <div className="mt-8 flex items-center justify-between">
                    <button onClick={back} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#0b1f3a] transition">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    {step < 5 && (
                      <button onClick={next} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0b1f3a] hover:text-[#b08e46] transition">
                        Skip <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Prefer to talk? Call <a href={`tel:${company.phoneTel}`} className="font-semibold text-[#0b1f3a] hover:text-[#b08e46]">{company.phoneDisplay}</a>
        </p>
      </div>
    </main>
  );
}
